import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import md5 from 'md5';
import PhoneInput, {
  isValidPhoneNumber,
} from 'react-phone-number-input/mobile';
import 'react-phone-number-input/style.css';
import { Button, CheckBox, Typography } from 'ck-ui';
import { isEmailValid } from 'ck-utils/validate';
import { getStoreLocation } from 'ck-utils';
import { usePhoneInputCountries } from './hooks';
import { klaviyoSignUp } from './utils';
import styles from './PopupSignup.module.scss';

const SignUpForm = ({ source, onSubmitSuccess, smsErrorMessage, campaignId, trackEvent, hasNameSection }) => {
  const [email, setEmail] = useState('');
  const [hashedEmail, setHashedEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [emailError, setEmailError] = useState(null);
  const [phoneError, setPhoneError] = useState(null);
  const [isFetching, setIsFetching] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [firstNameError, setFirstNameError] = useState(null);
  const [lastName, setLastName] = useState('');
  const [lastNameError, setLastNameError] = useState(null);
  const [consented, setConsented] = useState(false);
  const [consentedError, setConsentedError] = useState(null);
  const [preferences, setPreferences] = useState({
    men: false,
    women: false,
    kids: false,
  });
  const [smsConsent, setSMSConsent] = useState([]);
  const { defaultCountry, countries } = usePhoneInputCountries();
  const storeLocation = getStoreLocation();
  const validateEmail = () => {
    const isValid = isEmailValid(email);
    setHashedEmail(isValid ? md5(email.toLowerCase()) : '');
    setEmailError(isValid ? null : 'Please enter a valid email address.');
    return isValid;
  };

  const validatePhone = () => {
    // phone is optional for US
    if (storeLocation === 'US' && !phone) return true;
    const isValid = phone ? isValidPhoneNumber(phone) : false;
    setPhoneError(isValid ? null : `Please enter a valid mobile number${source === 'signup_popup' ? ', winner is notified via SMS' : ''}.`);
    return isValid;
  };

  const validateFirstName = () => {
    const isValid = firstName?.trim().length > 0;
    setFirstNameError(isValid ? null : 'Please enter your first name.');
    return isValid;
  };

  const validateLastName = () => {
    const isValid = lastName?.trim().length > 0;
    setLastNameError(isValid ? null : 'Please enter your last name.');
    return isValid;
  };

  const validateConsent = () => {
    setConsentedError(consented ? null : 'You will need to tick to accept the the conditions of entry.');
    return consented;
  };

  const changePreferences = (e) => {
    setPreferences({ ...preferences, [e.target.name]: e.target.checked });
  };

  const changeSmsConsent = (e) => {
    const { name, checked } = e.target;
    if (checked && !smsConsent.includes(name)) {
      setSMSConsent([...smsConsent, name]);
    } else {
      setSMSConsent(smsConsent.filter(item => item !== name));
    }
  };

  const onSubmit = e => {
    e.preventDefault();

    if ((campaignId || hasNameSection) && (!validateFirstName() || !validateLastName() || !validateConsent())) return;
    if (!validateEmail() || !validatePhone()) return;

    setIsFetching(true);
    const params = {
      email,
      phone,
      preferences,
      source,
      trackEvent,
      onSuccess: (values) => {
        const [, smsBumpResponse] = values;
        if (smsErrorMessage && smsBumpResponse?.data?.status === 'error') {
          setPhoneError(smsErrorMessage);
        } else {
          onSubmitSuccess();
        }
      },
      onFinal: () => setIsFetching(false)
    };
    if (hasNameSection) {
      params.firstName = firstName;
      params.lastName = lastName;
    }
    if (campaignId) {
      params.firstName = firstName;
      params.lastName = lastName;
      params[`${campaignId} User Consented`] = consented;
      params[`${campaignId} Unique Entry Code`] = md5(email.toLowerCase());
    }
    if (storeLocation === 'NZ') {
      params.smsConsent = smsConsent;
    }

    klaviyoSignUp(params);
  };

  return (
    <form className={styles.mediumMarginTop} onSubmit={onSubmit}>
      {(campaignId || hasNameSection) && (
        <div className={classnames('is-flex', styles.mediumMarginTop)}>
          <div className={classnames(styles.box, styles.marginRight)}>
            <label htmlFor="firstName"><Typography color="black-bis" weight={400}>First Name*</Typography></label>
            <input
              id="firstName"
              value={firstName}
              type="text"
              className={classnames(styles.input, 'body-small', 'is-full-width', { [styles.error]: firstNameError })}
              onChange={e => setFirstName(e.target.value)}
              onBlur={validateFirstName}
            />
            {firstNameError && (
              <Typography className="mt-1" variant="body-small" display="block" color="danger">
                {firstNameError}
              </Typography>
            )}
          </div>
          <div className={styles.box}>
            <label htmlFor="lastName"><Typography weight={400} color="black-bis">Last Name*</Typography></label>
            <input
              id="lastName"
              value={lastName}
              type="text"
              className={classnames(styles.input, 'body-small', 'is-full-width', { [styles.error]: lastNameError })}
              onChange={e => setLastName(e.target.value)}
              onBlur={validateLastName}
            />
            {lastNameError && (
              <Typography className="mt-1" variant="body-small" display="block" color="danger">
                {lastNameError}
              </Typography>
            )}
          </div>
        </div>
      )}

      <label htmlFor="email"><Typography color="black-bis" weight={400}>Email Address*</Typography></label>
      <input
        id="email"
        value={email}
        type="email"
        placeholder="Email Address"
        className={classnames(styles.input, 'is-inline-block', 'is-full-width', 'body-small', { [styles.error]: emailError })}
        onChange={e => setEmail(e.target.value)}
        onBlur={validateEmail}
      />
      {emailError && (
        <Typography className="mt-1" variant="body-small" display="block" color="danger">{emailError}</Typography>
      )}
      {campaignId && hashedEmail && (
        <Typography className="mt-1" variant="body-small" display="block" weight={700}>Unique Entry ID: {hashedEmail}</Typography>
      )}

      <div className={classnames('is-flex', styles.mediumMarginTop)}>
        <div className="is-full-width">
          <label htmlFor="phone"><Typography color="black-bis" weight={400}>Phone{storeLocation !== 'US' ? '*' : ' (Optional)'}</Typography></label>
          <PhoneInput
            id="phone"
            defaultCountry={defaultCountry}
            countries={countries}
            countryCallingCodeEditable={false}
            countrySelectProps={{ unicodeFlags: true }}
            value={phone}
            className={classnames(styles.phoneInput, 'is-full-width', { [styles.error]: phoneError })}
            onChange={setPhone}
            onBlur={validatePhone}
          />
          {phoneError && (
            <Typography className="mt-1" variant="body-small" display="block" color="danger">{phoneError}</Typography>
          )}
        </div>
      </div>

      <legend className="mt-2"><Typography weight={400} color="black-bis">Choose Your Product Preferences</Typography></legend>
      <label><CheckBox name="men" checked={preferences.men} onChange={changePreferences} className={styles.marginRight}>Men</CheckBox></label>
      <label><CheckBox name="women" checked={preferences.women} onChange={changePreferences} className={styles.marginRight}>Women</CheckBox></label>
      <label><CheckBox name="kids" checked={preferences.kids} onChange={changePreferences}>Kids</CheckBox></label>

      {storeLocation === 'NZ' && (
        <div className={styles.consentContainer}>
          <label>
            <CheckBox name="marketing" checked={smsConsent.includes('marketing')} onChange={changeSmsConsent} />
            <Typography className={styles.consentText}>
              I consent to receiving marketing text messages (e.g. cart reminders and back-in-stock notifications) at the number provided.
            </Typography>
          </label>
          <label>
            <CheckBox name="transactional" checked={smsConsent.includes('transactional')} onChange={changeSmsConsent} />
            <Typography className={styles.consentText}>
              I consent to receiving non-marketing updates and information, such as shipping and account updates at the number provided.
            </Typography>
          </label>
        </div>
      )}

      {(campaignId || hasNameSection) && (
        <div className={styles.mediumMarginTop}>
          <label>
            <CheckBox name="consented" checked={consented} onChange={() => setConsented(!consented)}>
              I have read and accepted the conditions of entry.
            </CheckBox>
          </label>
          {consentedError && (
            <Typography variant="body-small" display="block" color="danger">{consentedError}</Typography>
          )}
        </div>
      )}

      <div className="has-text-centered">
        <Button fullwidth={source === 'signup_popup' ? 'all' : 'mobile'} loading={isFetching} type="submit" size="large" className={styles.signUpBtn}>SIGN UP</Button>
      </div>
    </form>
  );
};

SignUpForm.propTypes = {
  source: PropTypes.string.isRequired,
  onSubmitSuccess: PropTypes.func.isRequired,
  smsErrorMessage: PropTypes.node,
  campaignId: PropTypes.string,
  trackEvent: PropTypes.func,
  hasNameSection: PropTypes.bool
};

SignUpForm.defaultProps = {
  smsErrorMessage: null,
  campaignId: null,
  trackEvent: null,
  hasNameSection: false
};

export { SignUpForm };
